import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Sidebar from "@/components/propgen/Sidebar";
import Markdown from "@/components/propgen/Markdown";
import DiffView from "@/components/propgen/DiffView";
import Timeline from "@/components/propgen/Timeline";
import SimilarProjects from "@/components/propgen/SimilarProjects";
import { api, formatApiError, API } from "@/lib/propgen_api";
import { useAuth } from "@/lib/propgen_auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Sparkles, Calculator, FileText, MessageSquare, History,
  Send, Save, Download, Trash2, AlertTriangle, ChevronRight, Zap, Check,
  HelpCircle, Mail, Plus, Copy,
} from "lucide-react";

const STEPS = [
  { key: "transcript", label: "Transcript", icon: Upload },
  { key: "requirements", label: "Scope", icon: Sparkles },
  { key: "questions", label: "Client Q's", icon: HelpCircle },
  { key: "estimate", label: "Estimate", icon: Calculator },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "review", label: "AI Review", icon: MessageSquare },
  { key: "versions", label: "Versions", icon: History },
];

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const hours = (n) => n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;

export default function Workspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canSeeInternal = !!user?.can_see_internal;
  const [project, setProject] = useState(null);
  const [step, setStep] = useState("transcript");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      // Auto-jump to latest step
      if (!step || step === "transcript") {
        if (data.documents && Object.keys(data.documents).length) setStep("documents");
        else if (data.estimate) setStep("estimate");
        else if (data.requirements) setStep("requirements");
        else setStep("transcript");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const refresh = async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
  };

  if (loading || !project) {
    return (
      <div className="flex min-h-screen">
        <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
        <main className="flex-1 p-8 text-sm text-muted-foreground" data-testid="workspace-loading">Loading project…</main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="workspace-page">
      <Sidebar onNewProject={() => nav("/superadmin/propgen")} />
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Project header */}
        <div className="border-b bg-card">
          <div className="px-8 pt-6 flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Link to="/superadmin/propgen" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="workspace-back-link">
                  <ArrowLeft className="w-3 h-3" /> All projects
                </Link>
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground truncate">{project.client_name}</span>
              </div>
              <h1 className="font-display text-3xl font-black tracking-tighter mt-2 truncate" data-testid="workspace-project-name">
                {project.name}
              </h1>
              {project.description && (
                <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{project.description}</p>
              )}
            </div>
            <div className="text-right shrink-0 flex items-start gap-3">
              {canSeeInternal && (
                <Link
                  to={`/superadmin/propgen/projects/${project.id}/emergent-estimate`}
                  data-testid="workspace-emergent-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[var(--peak-blue)] text-[var(--peak-blue)] hover:bg-[var(--peak-blue)] hover:text-white transition-colors duration-200 text-xs font-mono uppercase tracking-wider"
                  title="Internal build-cost forecast — credits + timeline (do not share with client)"
                >
                  <Zap className="w-3.5 h-3.5" />
                  Internal Build Cost
                </Link>
              )}
              <div>
                <div className="eyebrow">Status</div>
                <div className="font-display text-lg font-black tracking-tight mt-1 uppercase" data-testid="workspace-status">{project.status}</div>
              </div>
            </div>
          </div>

          {/* Step tabs */}
          <div className="px-8 mt-4 flex flex-wrap gap-0 -mb-px" data-testid="workspace-steps">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = step === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(s.key)}
                  data-testid={`step-${s.key}`}
                  className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider font-mono border-b-2 transition-colors duration-150 ${
                    active
                      ? "border-[var(--peak-blue)] text-foreground bg-background"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {step === "transcript" && <TranscriptStep project={project} refresh={refresh} onNext={() => setStep("requirements")} />}
          {step === "requirements" && <RequirementsStep project={project} refresh={refresh} onNext={() => setStep("questions")} />}
          {step === "questions" && <QuestionsStep project={project} refresh={refresh} onNext={() => setStep("estimate")} user={user} />}
          {step === "estimate" && <EstimateStep project={project} refresh={refresh} onNext={() => setStep("documents")} />}
          {step === "documents" && <DocumentsStep project={project} refresh={refresh} onNext={() => setStep("review")} canSeeInternal={canSeeInternal} />}
          {step === "review" && <ReviewStep project={project} refresh={refresh} canSeeInternal={canSeeInternal} />}
          {step === "versions" && <VersionsStep project={project} refresh={refresh} canSeeInternal={canSeeInternal} />}
        </div>
      </main>
    </div>
  );
}

/* ============================================================
   Step 1 · Transcript
   ============================================================ */
function TranscriptStep({ project, refresh, onNext }) {
  const fileRef = useRef(null);
  const [text, setText] = useState(project.transcript_text || "");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/projects/${project.id}/transcript/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText(data.transcript_text);
      toast.success(`Transcript uploaded (${data.chars.toLocaleString()} chars)`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setUploading(false);
  };

  const savePaste = async () => {
    setUploading(true);
    try {
      await api.post(`/projects/${project.id}/transcript/paste`, { text });
      toast.success("Transcript saved");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const runExtract = async () => {
    setExtracting(true);
    try {
      await api.post(`/projects/${project.id}/extract`);
      toast.success("AI extraction complete");
      await refresh();
      onNext();
    } catch (e) { toast.error(formatApiError(e)); }
    setExtracting(false);
  };

  return (
    <div className="p-8 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6" data-testid="transcript-step">
      <div>
        <div className="eyebrow">Step 1</div>
        <h2 className="font-display text-2xl font-black tracking-tight mt-1">Upload the discovery call</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          Drop a <span className="font-mono">.txt</span>, <span className="font-mono">.docx</span>, or <span className="font-mono">.pdf</span> transcript file — or paste raw text. Everything is stored securely.
        </p>

        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
          data-testid="transcript-dropzone"
          className="mt-6 h-64 border-2 border-dashed border-neutral-400 hover:border-[var(--peak-blue)] hover:bg-[var(--peak-blue)]/5 cursor-pointer flex flex-col items-center justify-center transition-colors duration-200 group"
        >
          <Upload className="w-8 h-8 text-neutral-500 group-hover:text-[var(--peak-blue)] transition-colors duration-200" />
          <div className="font-display font-bold mt-3">Drop file or click to upload</div>
          <div className="text-xs text-muted-foreground mt-1 font-mono">.txt · .docx · .pdf · max 15MB</div>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.docx,.pdf"
            data-testid="transcript-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
        </div>
        {project.transcript_filename && (
          <div className="mt-3 flex items-center gap-2 text-xs font-mono text-muted-foreground" data-testid="transcript-filename">
            <FileText className="w-3.5 h-3.5" /> {project.transcript_filename}
          </div>
        )}
        {uploading && <div className="text-xs text-muted-foreground mt-2">Uploading…</div>}
      </div>

      <div className="min-w-0">
        <div className="eyebrow">Transcript text</div>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={20}
          placeholder="Or paste transcript text here…"
          data-testid="transcript-textarea"
          className="mt-2 font-mono text-xs border-2 min-h-[420px]"
        />
        <div className="flex items-center justify-between mt-3 gap-3">
          <div className="text-xs text-muted-foreground font-mono">{text.length.toLocaleString()} chars</div>
          <div className="flex gap-2">
            <Button
              onClick={savePaste}
              disabled={!text.trim() || uploading}
              data-testid="transcript-save-btn"
              className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-colors duration-200 font-display font-bold text-xs uppercase tracking-wide"
            >
              <Save className="w-4 h-4 mr-2" /> Save Text
            </Button>
            <Button
              onClick={runExtract}
              disabled={!text.trim() || extracting}
              data-testid="run-extract-btn"
              className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4 mr-2" /> {extracting ? "Extracting…" : "Run AI Extraction →"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Step 2 · Requirements
   ============================================================ */
function RequirementsStep({ project, refresh, onNext }) {
  const r = project.requirements;
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState(r);

  useEffect(() => { setLocal(r); }, [r]);

  if (!r) {
    return (
      <div className="p-8" data-testid="requirements-empty">
        <div className="border-2 border-dashed border-neutral-300 p-10 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display font-bold mt-3">No requirements extracted yet</div>
          <p className="text-sm text-muted-foreground mt-2">Go back to Transcript and click <b>Run AI Extraction</b>.</p>
        </div>
      </div>
    );
  }

  const patchFeature = (i, key, val) => {
    const next = { ...local };
    next.features = [...(next.features || [])];
    next.features[i] = { ...next.features[i], [key]: val };
    setLocal(next);
  };
  const removeFeature = (i) => {
    const next = { ...local, features: local.features.filter((_, idx) => idx !== i) };
    setLocal(next);
  };
  const addFeature = () => {
    const next = { ...local, features: [...(local.features || []), { name: "New feature", category: "core", complexity: "medium", description: "" }] };
    setLocal(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}`, { requirements: local });
      toast.success("Requirements saved");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="p-8 space-y-6" data-testid="requirements-step">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Step 2</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Review extracted scope</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Edit anything the AI got wrong. This drives the estimate.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} data-testid="requirements-save-btn" className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-colors duration-200 font-display font-bold text-xs uppercase tracking-wide">
            <Save className="w-4 h-4 mr-2" /> Save
          </Button>
          <Button onClick={() => { save(); onNext(); }} data-testid="requirements-next-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide transition-colors duration-200">
            Follow-up Questions →
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SummaryCard label="Product Type" value={local.product_type || "—"} testid="req-product-type" />
        <SummaryCard label="Design Complexity" testid="req-design">
          <Select value={local.design_complexity || "medium"} onValueChange={(v) => setLocal({ ...local, design_complexity: v })}>
            <SelectTrigger data-testid="req-design-select" className="h-10 border-2 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </SummaryCard>
        <SummaryCard label="Urgency" testid="req-urgency">
          <Select value={local.urgency || "medium"} onValueChange={(v) => setLocal({ ...local, urgency: v })}>
            <SelectTrigger data-testid="req-urgency-select" className="h-10 border-2 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </SummaryCard>
      </div>

      <div className="border bg-card">
        <div className="p-4 border-b bg-muted/30">
          <div className="eyebrow">Project summary</div>
          <p className="text-sm mt-1">{local.project_summary || "—"}</p>
        </div>
      </div>

      <div className="border bg-card" data-testid="features-table">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <div className="eyebrow">Features ({(local.features || []).length})</div>
          <button onClick={addFeature} data-testid="add-feature-btn" className="text-xs font-mono uppercase tracking-wider hover:text-[var(--peak-blue)] transition-colors">+ Add Feature</button>
        </div>
        <div className="grid grid-cols-[2fr_1.5fr_120px_120px_40px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
          <div>Feature</div><div>Description</div><div>Category</div><div>Complexity</div><div></div>
        </div>
        {(local.features || []).map((f, i) => (
          <div key={i} className="grid grid-cols-[2fr_1.5fr_120px_120px_40px] items-center px-4 py-2 border-b last:border-b-0 gap-2" data-testid={`feature-row-${i}`}>
            <Input value={f.name || ""} onChange={(e) => patchFeature(i, "name", e.target.value)} className="h-9 border-2 text-sm" data-testid={`feature-name-${i}`} />
            <Input value={f.description || ""} onChange={(e) => patchFeature(i, "description", e.target.value)} className="h-9 border-2 text-xs text-muted-foreground" data-testid={`feature-desc-${i}`} />
            <Select value={f.category || "core"} onValueChange={(v) => patchFeature(i, "category", v)}>
              <SelectTrigger className="h-9 border-2 text-xs" data-testid={`feature-category-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["core","integration","ai","admin","design","infra"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={f.complexity || "medium"} onValueChange={(v) => patchFeature(i, "complexity", v)}>
              <SelectTrigger className="h-9 border-2 text-xs" data-testid={`feature-complexity-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low","medium","high"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => removeFeature(i)} data-testid={`feature-remove-${i}`} className="text-muted-foreground hover:text-red-600 transition-colors flex justify-end"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!(local.features || []).length && <div className="p-6 text-sm text-muted-foreground text-center">No features extracted yet — add manually or re-run extraction.</div>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ListCard label="Integrations" items={local.integrations || []} testid="req-integrations" />
        <ListCard label="Assumptions" items={local.assumptions || []} testid="req-assumptions" />
        <ListCard label="Missing Info" items={local.missing_info || []} tone="warn" testid="req-missing" />
      </div>
      <ListCard label="Risks" items={local.risks || []} tone="danger" testid="req-risks" />
    </div>
  );
}

function SummaryCard({ label, value, children, testid }) {
  return (
    <div className="border bg-card p-4" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      {children || <div className="font-display text-lg font-bold mt-1">{value}</div>}
    </div>
  );
}

function ListCard({ label, items, tone, testid }) {
  const toneCls =
    tone === "danger" ? "border-red-500 bg-red-50 dark:bg-red-950/20" :
    tone === "warn"   ? "border-amber-400 bg-amber-50 dark:bg-amber-950/20" :
    "";
  return (
    <div className={`border bg-card p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        {tone === "danger" && <AlertTriangle className="w-3.5 h-3.5 text-red-600" />}
        <div className="eyebrow">{label} ({items.length})</div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground mt-2 italic">None</div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map((it, i) => <li key={i} className="text-xs leading-relaxed">• {it}</li>)}
        </ul>
      )}
    </div>
  );
}

/* ============================================================
   Step 2.5 · Client Questions (follow-up email builder)
   ============================================================ */
const QUESTION_CATEGORY_META = {
  scope:        { label: "Scope & Requirements",     color: "var(--peak-blue)" },
  integrations: { label: "Integrations & 3rd-party", color: "var(--peak-blue)" },
  timeline:     { label: "Timeline & Delivery",      color: "#111" },
  budget:       { label: "Budget & Investment",      color: "#111" },
  technical:    { label: "Technical Constraints",    color: "var(--peak-blue)" },
  ux:           { label: "User Experience & Design", color: "var(--peak-blue)" },
  data:         { label: "Data & Volumes",           color: "#111" },
  compliance:   { label: "Compliance & Security",    color: "#B91C1C" },
  other:        { label: "Other",                    color: "#666" },
};

function QuestionsStep({ project, refresh, onNext, user }) {
  const [questions, setQuestions] = useState(project.client_questions || []);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [clientEmail, setClientEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("scope");
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const [showIngest, setShowIngest] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [ingestState, setIngestState] = useState(null); // {stage, matches, loading}
  const [applyingScope, setApplyingScope] = useState(false);

  useEffect(() => { setQuestions(project.client_questions || []); }, [project.client_questions]);

  const includedCount = questions.filter((q) => q.included).length;
  const answeredCount = questions.filter((q) => (q.answer || "").trim()).length;
  const unansweredIncluded = questions.filter((q) => q.included && !(q.answer || "").trim()).length;

  const generate = async (merge = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/client-questions/generate`, { merge_with_existing: merge });
      setQuestions(data.questions);
      toast.success(`${data.generated_count} new question${data.generated_count === 1 ? "" : "s"} generated`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setGenerating(false);
  };

  const patchQ = (i, patch) => {
    const next = [...questions];
    next[i] = { ...next[i], ...patch };
    setQuestions(next);
  };
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const addManual = () => {
    if (!newText.trim()) return;
    setQuestions([...questions, {
      id: `local-${Date.now()}`,
      question: newText.trim(),
      category: newCategory,
      source: "manual",
      included: true,
      rationale: "",
    }]);
    setNewText("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/projects/${project.id}/client-questions`, { questions });
      setQuestions(data);
      toast.success("Questions saved");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const openIngest = () => {
    setReplyText("");
    setIngestState(null);
    setShowIngest(true);
  };

  const runIngest = async () => {
    if (!replyText.trim()) { toast.error("Paste the client reply first"); return; }
    setIngestState({ stage: "loading" });
    try {
      // Save latest edits first so IDs are stable
      await api.patch(`/projects/${project.id}/client-questions`, { questions });
      const { data } = await api.post(`/projects/${project.id}/client-questions/ingest-reply`, { reply_text: replyText });
      const matches = (data.matches || []).map((m) => ({ ...m, accept: !!m.matched_answer }));
      setIngestState({ stage: "review", matches });
    } catch (e) {
      toast.error(formatApiError(e));
      setIngestState(null);
    }
  };

  const applyIngest = async () => {
    if (!ingestState?.matches) return;
    const toApply = ingestState.matches.filter((m) => m.accept && m.matched_answer);
    if (toApply.length === 0) { toast.error("Nothing to apply"); return; }
    try {
      const { data } = await api.post(`/projects/${project.id}/client-questions/apply-matches`, { matches: toApply });
      setQuestions(data.questions);
      toast.success(`${data.applied} answer${data.applied === 1 ? "" : "s"} applied`);
      setShowIngest(false);
      setIngestState(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyToScope = async () => {
    if (!window.confirm(`Re-run AI extraction using the transcript + ${answeredCount} answered question(s)? This will update the scope, which then reflows into estimates. (Prior state is captured by the versions system.)`)) return;
    setApplyingScope(true);
    try {
      await api.post(`/projects/${project.id}/client-questions/apply-to-scope`);
      toast.success("Scope refreshed with client answers");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setApplyingScope(false);
  };

  const openEmail = async () => {
    if (includedCount === 0) { toast.error("Include at least one question first"); return; }
    // Save latest edits before drafting
    setEmailLoading(true);
    setShowEmail(true);
    try {
      await api.patch(`/projects/${project.id}/client-questions`, { questions });
      const { data } = await api.post(`/projects/${project.id}/client-questions/email-draft`, {
        client_email: clientEmail || undefined,
        sender_name: user?.name || undefined,
      });
      setEmailDraft(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setShowEmail(false);
    }
    setEmailLoading(false);
  };

  const copyBody = async () => {
    if (!emailDraft) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${emailDraft.subject}\n\n${emailDraft.body}`);
      toast.success("Copied subject + body to clipboard");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };
  const openMailto = () => {
    if (!emailDraft) return;
    const params = new URLSearchParams({ subject: emailDraft.subject, body: emailDraft.body });
    window.location.href = `mailto:${clientEmail}?${params.toString()}`;
  };

  // Group for display
  const grouped = {};
  questions.forEach((q, idx) => {
    const cat = QUESTION_CATEGORY_META[q.category] ? q.category : "other";
    (grouped[cat] ||= []).push({ ...q, _idx: idx });
  });

  return (
    <div className="p-8 space-y-6" data-testid="questions-step">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Step 3</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Follow-up questions for the client</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            AI drafts the sharpest questions to send back. Curate, add your own, then export as a ready-to-send email.
            <b className="text-foreground"> Better answers = tighter quote.</b>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {questions.length === 0 ? (
            <Button onClick={() => generate(false)} disabled={generating} data-testid="generate-questions-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
              <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Generating…" : "Generate Questions"}
            </Button>
          ) : (
            <>
              <Button onClick={() => generate(true)} disabled={generating} data-testid="regenerate-questions-btn" className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide">
                <Sparkles className="w-4 h-4 mr-2" /> {generating ? "…" : "Suggest More"}
              </Button>
              <Button onClick={save} disabled={saving} data-testid="save-questions-btn" className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide">
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button onClick={openIngest} data-testid="ingest-reply-btn" className="h-10 border-2 border-[var(--peak-blue)] bg-white text-[var(--peak-blue)] hover:bg-[var(--peak-blue)] hover:text-white text-xs uppercase font-display font-bold tracking-wide transition-colors">
                <Mail className="w-4 h-4 mr-2" /> Paste Client Reply
              </Button>
              {unansweredIncluded > 0 && (
                <Button onClick={openEmail} data-testid="draft-email-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
                  <Mail className="w-4 h-4 mr-2" /> Draft Email ({unansweredIncluded})
                </Button>
              )}
              {answeredCount > 0 && (
                <Button onClick={applyToScope} disabled={applyingScope} data-testid="apply-to-scope-btn" className="h-10 bg-black hover:bg-[var(--peak-blue)] text-white font-display font-bold text-xs uppercase tracking-wide transition-colors">
                  <Sparkles className="w-4 h-4 mr-2" /> {applyingScope ? "Refreshing scope…" : `Apply ${answeredCount} to Scope`}
                </Button>
              )}
              <Button onClick={onNext} data-testid="questions-next-btn" className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide">
                Estimate →
              </Button>
            </>
          )}
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="border-2 border-dashed p-12 text-center max-w-xl mx-auto">
          <HelpCircle className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display font-bold mt-3 text-xl">No follow-up questions yet</div>
          <p className="text-sm text-muted-foreground mt-2">
            Click <b>Generate Questions</b> — GPT-5.2 will read the extracted scope + missing_info and produce 8-15 targeted questions to send the client, grouped by category.
          </p>
        </div>
      ) : (
        <>
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            {questions.length} total · <span className="text-[var(--peak-blue)]">{includedCount} in email</span> · <span className="text-emerald-700 dark:text-emerald-400">{answeredCount} answered</span> · {questions.length - includedCount} excluded
          </div>

          {Object.keys(QUESTION_CATEGORY_META).filter((c) => grouped[c]).map((cat) => (
            <div key={cat} className="border bg-card" data-testid={`questions-cat-${cat}`}>
              <div className="px-4 py-2 border-b bg-muted/30 flex items-center gap-2">
                <span className="w-2 h-2" style={{ background: QUESTION_CATEGORY_META[cat].color }} />
                <div className="eyebrow">{QUESTION_CATEGORY_META[cat].label}</div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground ml-auto">
                  {grouped[cat].length} question{grouped[cat].length === 1 ? "" : "s"}
                </span>
              </div>
              {grouped[cat].map((q) => {
                const isAnswered = !!(q.answer || "").trim();
                const isExpanded = expandedAnswers[q.id] || isAnswered;
                return (
                  <div key={q.id || q._idx} className={`border-b last:border-b-0 ${isAnswered ? "bg-emerald-50/40 dark:bg-emerald-950/10" : ""}`} data-testid={`question-row-${q._idx}`}>
                    <div className="grid grid-cols-[40px_1fr_120px_40px] items-start px-4 py-3 gap-3">
                      <div className="pt-2">
                        <input
                          type="checkbox"
                          checked={!!q.included}
                          onChange={(e) => patchQ(q._idx, { included: e.target.checked })}
                          data-testid={`question-include-${q._idx}`}
                          className="w-4 h-4 accent-[var(--peak-blue)]"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {isAnswered && (
                            <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-emerald-600 text-emerald-700 dark:text-emerald-400" data-testid={`question-answered-${q._idx}`}>
                              <Check className="w-2.5 h-2.5" /> Answered
                              {q.answered_via && <span className="text-[8px] opacity-70">· {q.answered_via.replace("_"," ")}</span>}
                            </span>
                          )}
                        </div>
                        <Textarea
                          value={q.question}
                          onChange={(e) => patchQ(q._idx, { question: e.target.value })}
                          rows={1}
                          data-testid={`question-text-${q._idx}`}
                          className={`w-full border-2 text-sm resize-none ${q.included ? "" : "opacity-50"}`}
                        />
                        {q.rationale && (
                          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1 italic">
                            Why: {q.rationale}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => setExpandedAnswers({ ...expandedAnswers, [q.id]: !isExpanded })}
                          data-testid={`question-toggle-answer-${q._idx}`}
                          className="mt-2 text-[10px] font-mono uppercase tracking-wider text-[var(--peak-blue)] hover:underline"
                        >
                          {isExpanded ? "▼ Hide answer" : "▸ Add answer"}
                        </button>
                        {isExpanded && (
                          <div className="mt-2 border-l-2 border-emerald-500 pl-3">
                            <div className="eyebrow mb-1 text-[9px]">Client answer</div>
                            <Textarea
                              value={q.answer || ""}
                              onChange={(e) => patchQ(q._idx, {
                                answer: e.target.value,
                                answered_via: e.target.value.trim() && !q.answered_via ? "manual" : q.answered_via,
                                answered_at: e.target.value.trim() ? (q.answered_at || new Date().toISOString()) : null,
                              })}
                              rows={2}
                              placeholder="Type or paste the client's answer here…"
                              data-testid={`question-answer-${q._idx}`}
                              className="w-full border-2 text-sm resize-none bg-white dark:bg-neutral-950"
                            />
                          </div>
                        )}
                      </div>
                      <Select value={q.category} onValueChange={(v) => patchQ(q._idx, { category: v })}>
                        <SelectTrigger className="h-9 border-2 text-xs" data-testid={`question-cat-${q._idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(QUESTION_CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeQ(q._idx)} data-testid={`question-remove-${q._idx}`} className="text-muted-foreground hover:text-red-600 transition-colors self-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Add manual */}
          <div className="border-2 border-dashed p-4" data-testid="add-question-form">
            <div className="eyebrow mb-2">Add your own question</div>
            <div className="grid grid-cols-[1fr_160px_100px] gap-2 items-end">
              <Input
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="e.g. Do you have a preferred hosting provider or existing AWS account?"
                data-testid="add-question-input"
                className="h-10 border-2 text-sm"
              />
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="h-10 border-2 text-xs" data-testid="add-question-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(QUESTION_CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={addManual} disabled={!newText.trim()} data-testid="add-question-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Email preview dialog */}
      <Dialog open={showEmail} onOpenChange={(v) => { if (!v) { setShowEmail(false); setEmailDraft(null); } }}>
        <DialogContent className="max-w-3xl w-[92vw]" data-testid="email-draft-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <Mail className="w-5 h-5 text-[var(--peak-blue)]" /> Client follow-up email
            </DialogTitle>
          </DialogHeader>
          {emailLoading || !emailDraft ? (
            <div className="py-16 text-center text-sm font-mono uppercase tracking-wider text-muted-foreground">
              Drafting email…
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 border-b pb-2">
                <div className="eyebrow">To:</div>
                <Input
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  type="email"
                  placeholder="contact@client.com (optional — used for mailto link)"
                  data-testid="email-to-input"
                  className="h-9 border-2 font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 border-b pb-2">
                <div className="eyebrow">Subject:</div>
                <div className="font-mono text-sm break-words" data-testid="email-subject">{emailDraft.subject}</div>
              </div>
              <div>
                <div className="eyebrow mb-2">Body ({emailDraft.included_count} question{emailDraft.included_count === 1 ? "" : "s"})</div>
                <div className="border p-4 bg-muted/20 max-h-[50vh] overflow-auto text-sm whitespace-pre-wrap font-mono" data-testid="email-body">
                  {emailDraft.body}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setShowEmail(false)} data-testid="email-close-btn" className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors">
              Close
            </button>
            <Button
              onClick={copyBody}
              disabled={!emailDraft}
              data-testid="email-copy-btn"
              className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide"
            >
              <Copy className="w-4 h-4 mr-2" /> Copy subject + body
            </Button>
            <Button
              onClick={openMailto}
              disabled={!emailDraft}
              data-testid="email-mailto-btn"
              className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide"
            >
              <Mail className="w-4 h-4 mr-2" /> Open in mail client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Paste client reply ingestion dialog */}
      <Dialog open={showIngest} onOpenChange={(v) => { if (!v) { setShowIngest(false); setIngestState(null); } }}>
        <DialogContent className="max-w-4xl w-[94vw]" data-testid="ingest-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <Mail className="w-5 h-5 text-[var(--peak-blue)]" /> Ingest client reply
            </DialogTitle>
          </DialogHeader>
          {!ingestState && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                Paste the client&apos;s email reply below. GPT-5.2 will match each answer to its corresponding question — you review before saving.
              </div>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={12}
                placeholder="Paste the full email reply here…"
                data-testid="ingest-reply-textarea"
                className="border-2 font-mono text-xs"
              />
            </div>
          )}
          {ingestState?.stage === "loading" && (
            <div className="py-16 text-center text-sm font-mono uppercase tracking-wider text-muted-foreground">
              <div className="inline-flex items-center gap-2">
                <div className="w-2 h-2 bg-[var(--peak-blue)] animate-pulse" />
                Matching answers to questions…
              </div>
            </div>
          )}
          {ingestState?.stage === "review" && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                {ingestState.matches.filter(m => m.matched_answer).length} of {ingestState.matches.length} questions matched. Uncheck any incorrect match, edit the answer if needed, then Apply.
              </div>
              <div className="border max-h-[55vh] overflow-auto" data-testid="ingest-matches">
                {ingestState.matches.map((m, i) => {
                  const q = questions.find((qq) => qq.id === m.question_id);
                  if (!q) return null;
                  const confColor = m.confidence === "high" ? "text-emerald-700 border-emerald-600"
                                  : m.confidence === "medium" ? "text-amber-700 border-amber-600"
                                  : m.confidence === "low" ? "text-red-700 border-red-500"
                                  : "text-muted-foreground border-neutral-400";
                  const hasAnswer = !!(m.matched_answer || "").trim();
                  return (
                    <div key={m.question_id} className="grid grid-cols-[36px_1fr] gap-3 p-3 border-b last:border-b-0" data-testid={`ingest-match-${i}`}>
                      <div className="pt-2">
                        <input
                          type="checkbox"
                          checked={!!m.accept}
                          disabled={!hasAnswer}
                          onChange={(e) => {
                            const next = [...ingestState.matches];
                            next[i] = { ...next[i], accept: e.target.checked };
                            setIngestState({ ...ingestState, matches: next });
                          }}
                          data-testid={`ingest-accept-${i}`}
                          className="w-4 h-4 accent-[var(--peak-blue)]"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-medium mb-1">{q.question}</div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${confColor}`}>
                            {m.confidence || "none"}
                          </span>
                          {!hasAnswer && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground italic">Not addressed in reply</span>
                          )}
                        </div>
                        <Textarea
                          value={m.matched_answer || ""}
                          onChange={(e) => {
                            const next = [...ingestState.matches];
                            next[i] = { ...next[i], matched_answer: e.target.value, accept: e.target.value.trim() ? next[i].accept : false };
                            setIngestState({ ...ingestState, matches: next });
                          }}
                          rows={2}
                          placeholder="No answer extracted — you can type one manually."
                          data-testid={`ingest-answer-${i}`}
                          className="w-full border-2 text-xs resize-none font-mono"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => { setShowIngest(false); setIngestState(null); }} data-testid="ingest-close-btn" className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors">
              Cancel
            </button>
            {!ingestState && (
              <Button onClick={runIngest} disabled={!replyText.trim()} data-testid="ingest-run-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide">
                <Sparkles className="w-4 h-4 mr-2" /> Match answers to questions
              </Button>
            )}
            {ingestState?.stage === "review" && (
              <Button
                onClick={applyIngest}
                disabled={ingestState.matches.filter((m) => m.accept && m.matched_answer).length === 0}
                data-testid="ingest-apply-btn"
                className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide"
              >
                <Check className="w-4 h-4 mr-2" /> Apply {ingestState.matches.filter((m) => m.accept && m.matched_answer).length} answer(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================================================
   Step 3 · Estimate
   ============================================================ */
function EstimateStep({ project, refresh, onNext }) {
  const [params, setParams] = useState(project.estimate_params || {});
  const [estimate, setEstimate] = useState(project.estimate || null);
  const [computing, setComputing] = useState(false);

  useEffect(() => { setParams(project.estimate_params || {}); setEstimate(project.estimate || null); }, [project]);

  const compute = async (overrides = {}) => {
    setComputing(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/estimate/compute`, {
        hourly_rate_external: params.hourly_rate_external,
        hourly_rate_internal: params.hourly_rate_internal,
        buffer_pct: params.buffer_pct,
        target_margin_pct: params.target_margin_pct,
        team_size: params.team_size,
        ...overrides,
      });
      setEstimate(data.estimate);
      setParams(data.params);
      await refresh();
      toast.success("Estimate updated");
    } catch (e) { toast.error(formatApiError(e)); }
    setComputing(false);
  };

  return (
    <div className="p-8 space-y-6" data-testid="estimate-step">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Step 3</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Estimate engine</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Tune parameters and recompute. Formula respects design + urgency multipliers.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => compute()} disabled={computing} data-testid="compute-estimate-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
            <Calculator className="w-4 h-4 mr-2" /> {computing ? "Computing…" : "Recompute"}
          </Button>
          <Button onClick={onNext} disabled={!estimate} data-testid="estimate-next-btn" className="h-10 border-2 border-black bg-white text-black hover:bg-black hover:text-white transition-colors duration-200 font-display font-bold text-xs uppercase tracking-wide">
            Generate Documents →
          </Button>
        </div>
      </div>

      {/* Params */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "hourly_rate_external", label: "External Rate ($/h)", step: 5 },
          { key: "hourly_rate_internal", label: "Internal Rate ($/h)", step: 5 },
          { key: "target_margin_pct",    label: "Target Margin %",    step: 1 },
          { key: "buffer_pct",           label: "Buffer %",            step: 1 },
          { key: "team_size",            label: "Team Size",           step: 1 },
        ].map((f) => (
          <div key={f.key} className="border bg-card p-3" data-testid={`param-${f.key}`}>
            <div className="eyebrow">{f.label}</div>
            <Input
              type="number"
              step={f.step}
              value={params[f.key] ?? ""}
              onChange={(e) => setParams({ ...params, [f.key]: Number(e.target.value) })}
              onBlur={() => compute()}
              className="h-9 border-2 mt-1 font-mono text-sm"
              data-testid={`param-${f.key}-input`}
            />
          </div>
        ))}
      </div>

      {estimate ? (
        <>
          {/* Summary bar */}
          <div className="grid grid-cols-1 md:grid-cols-4 border bg-card" data-testid="estimate-summary">
            {[
              { label: "Total Hours",      value: hours(estimate.total_hours), tone: "" },
              { label: "External Price",   value: money(estimate.external_price), tone: "primary" },
              { label: "Internal Cost",    value: money(estimate.internal_cost), tone: "internal" },
              { label: "Timeline",         value: `${estimate.timeline_weeks_low}–${estimate.timeline_weeks_high} wks` },
            ].map((k, i) => (
              <div key={k.label} className={`p-5 ${i < 3 ? "border-r" : ""} ${k.tone === "internal" ? "pattern-internal" : ""}`}>
                <div className="eyebrow">{k.label}</div>
                <div className={`font-display text-3xl font-black tracking-tighter mt-1 font-mono ${k.tone === "primary" ? "text-[var(--peak-blue)]" : ""}`}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Similar past proposals — sanity check */}
          <SimilarProjects projectId={project.id} limit={3} />

          {/* Line item table */}
          <div className="border bg-card overflow-x-auto" data-testid="estimate-line-items">
            <div className="grid grid-cols-[2fr_120px_120px_100px_140px_140px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground min-w-[900px]">
              <div>Feature</div><div>Category</div><div>Complexity</div><div className="text-right">Hours</div><div className="text-right">Ext. Price</div><div className="text-right">Int. Cost</div>
            </div>
            {estimate.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-[2fr_120px_120px_100px_140px_140px] items-center px-4 py-2 border-b last:border-b-0 text-sm min-w-[900px]" data-testid={`estimate-row-${i}`}>
                <div className="truncate">{li.name}</div>
                <div className="font-mono text-xs uppercase text-muted-foreground">{li.category}</div>
                <div className="font-mono text-xs uppercase">{li.complexity}</div>
                <div className="font-mono text-right">{hours(li.hours)}</div>
                <div className="font-mono text-right">{money(li.external_price)}</div>
                <div className="font-mono text-right pattern-internal px-2">{money(li.internal_cost)}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="border-2 border-dashed p-10 text-center">
          <Calculator className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display font-bold mt-3">No estimate yet</div>
          <div className="text-sm text-muted-foreground mt-1">Click <b>Recompute</b> above to build one.</div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Step 4 · Documents
   ============================================================ */
const DOC_META_ALL = {
  sow:      { label: "Scope of Work",  audience: "Client" },
  proposal: { label: "Proposal",       audience: "Client" },
  quote:    { label: "Quote",          audience: "Client" },
  internal: { label: "Internal Doc",   audience: "Internal" },
};

// DOC_META is filtered per-user by internal-access permission.
const DOC_META = DOC_META_ALL;

const filterDocsForUser = (docs, canSeeInternal) => {
  if (canSeeInternal) return docs || {};
  const out = {};
  Object.entries(docs || {}).forEach(([k, v]) => { if (k !== "internal") out[k] = v; });
  return out;
};

function DocumentsStep({ project, refresh, canSeeInternal = true }) {
  const docs = filterDocsForUser(project.documents, canSeeInternal);
  const visibleDocMeta = Object.fromEntries(Object.entries(DOC_META_ALL).filter(([k]) => k in docs || canSeeInternal));
  const has = Object.keys(docs).length > 0;
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(Object.keys(visibleDocMeta)[0] || "sow");

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/projects/${project.id}/documents/generate`);
      toast.success("All 4 documents generated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setGenerating(false);
  };

  if (!has) {
    return (
      <div className="p-8" data-testid="documents-empty">
        <div className="border-2 border-dashed border-neutral-300 p-12 text-center max-w-xl mx-auto">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display font-bold mt-3 text-xl">Generate proposal documents</div>
          <p className="text-sm text-muted-foreground mt-2">SOW · Proposal · Quote · Internal Delivery Doc — all drafted by GPT-5.2 based on scope + estimate.</p>
          <Button onClick={generate} disabled={generating} data-testid="generate-docs-btn" className="mt-6 h-11 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
            <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Generating…" : "Generate 4 Documents"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full" data-testid="documents-step">
      <Tabs value={active} onValueChange={setActive} className="h-full flex flex-col">
        <div className="px-8 pt-4 flex items-center justify-between gap-4 border-b bg-card">
          <TabsList className="bg-transparent p-0 gap-0 h-auto">
            {Object.entries(visibleDocMeta).map(([k, m]) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`doc-tab-${k}`}
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-3 border-b-2 border-transparent data-[state=active]:border-[var(--peak-blue)] text-xs uppercase tracking-wider font-mono transition-colors duration-150"
              >
                {m.audience === "Internal" && <AlertTriangle className="w-3 h-3 mr-1.5 text-red-600" />}
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="pb-2 flex gap-2">
            <Button onClick={generate} disabled={generating} data-testid="regenerate-docs-btn" className="h-9 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide transition-colors">
              <Sparkles className="w-3.5 h-3.5 mr-2" /> {generating ? "…" : "Regenerate All"}
            </Button>
          </div>
        </div>
        {Object.keys(visibleDocMeta).map((k) => (
          <TabsContent key={k} value={k} className="flex-1 min-h-0 m-0">
            <DocEditor project={project} docType={k} refresh={refresh} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function DocEditor({ project, docType, refresh }) {
  const isInternal = docType === "internal";
  const doc = project.documents?.[docType];
  const [content, setContent] = useState(doc?.content || "");
  const [mode, setMode] = useState("preview");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setContent(doc?.content || ""); }, [doc?.content]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${project.id}/documents/${docType}`, { content });
      toast.success("Saved");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const [includeForecast, setIncludeForecast] = useState(true);

  const download = async () => {
    try {
      const url = new URL(`${API}/projects/${project.id}/documents/${docType}/export`);
      if (isInternal && includeForecast) url.searchParams.set("include_forecast", "true");
      const res = await fetch(url.toString(), {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("propgen_token") || ""}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `${project.name.replace(/\s+/g, "_")}_${docType}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { toast.error(e.message || "Export failed"); }
  };

  return (
    <div className={`h-full flex flex-col ${isInternal ? "pattern-internal" : ""}`} data-testid={`doc-editor-${docType}`}>
      {isInternal && (
        <div className="border-b bg-red-50 dark:bg-red-950/30 px-8 py-2 flex items-center gap-2 text-xs font-mono text-red-800 dark:text-red-200" data-testid="internal-warning">
          <AlertTriangle className="w-3.5 h-3.5" /> INTERNAL ONLY · DO NOT SHARE WITH CLIENT
        </div>
      )}
      <div className="px-8 py-3 border-b bg-card flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setMode("preview")} data-testid="doc-mode-preview" className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border ${mode === "preview" ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"} transition-colors`}>Preview</button>
          <button onClick={() => setMode("edit")} data-testid="doc-mode-edit" className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider border ${mode === "edit" ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"} transition-colors`}>Edit Markdown</button>
        </div>
        <div className="flex gap-2 items-center">
          {isInternal && (
            <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider cursor-pointer select-none" title="Append internal build-cost forecast to the exported PDF" data-testid="include-forecast-toggle">
              <input
                type="checkbox"
                checked={includeForecast}
                onChange={(e) => setIncludeForecast(e.target.checked)}
                className="w-3 h-3 accent-[var(--peak-blue)]"
              />
              +Forecast
            </label>
          )}
          <Button onClick={save} disabled={saving} data-testid={`doc-save-${docType}`} className="h-9 border-2 border-black bg-white text-black hover:bg-black hover:text-white text-xs uppercase font-display font-bold tracking-wide">
            <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "…" : "Save"}
          </Button>
          <Button onClick={download} data-testid={`doc-export-${docType}`} className="h-9 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white text-xs uppercase font-display font-bold tracking-wide">
            <Download className="w-3.5 h-3.5 mr-2" /> Export PDF
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto bg-background">
        {mode === "preview" ? (
          <div className="mx-auto max-w-3xl bg-card border my-6 p-10">
            <Markdown text={content} />
          </div>
        ) : (
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            data-testid={`doc-textarea-${docType}`}
            className="w-full h-full min-h-[70vh] font-mono text-xs border-0 rounded-none p-6 focus-visible:ring-0"
          />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Step 5 · AI Review (split pane)
   ============================================================ */
function ReviewStep({ project, refresh, canSeeInternal = true }) {
  const [msgs, setMsgs] = useState(project.chat_history || []);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const visibleDocs = filterDocsForUser(project.documents, canSeeInternal);
  const [docType, setDocType] = useState(Object.keys(visibleDocs)[0] || "proposal");
  const scrollRef = useRef(null);
  const [reviseState, setReviseState] = useState(null); // { docType, instruction, original, revised, loading }

  useEffect(() => { setMsgs(project.chat_history || []); }, [project.chat_history]);
  const openRevise = (instruction) => {
    // Open the modal with default selection = current doc.
    // User can select more docs (cascade) and then click "Generate revisions".
    const availableDocs = Object.keys(visibleDocs).filter(
      (d) => (project.documents?.[d]?.content || "").trim().length > 0
    );
    if (availableDocs.length === 0) {
      toast.error("Generate documents first");
      return;
    }
    setReviseState({
      stage: "select",
      instruction,
      selected: [availableDocs.includes(docType) ? docType : availableDocs[0]],
      availableDocs,
      docs: null, // will hold { doc_type: {original, revised} }
      activeTab: null,
      loading: false,
    });
  };

  const toggleReviseDoc = (dt) => {
    if (!reviseState) return;
    const next = reviseState.selected.includes(dt)
      ? reviseState.selected.filter((x) => x !== dt)
      : [...reviseState.selected, dt];
    // Preserve availableDocs order to keep the tabs consistent
    const ordered = reviseState.availableDocs.filter((d) => next.includes(d));
    setReviseState({ ...reviseState, selected: ordered });
  };

  const runRevise = async () => {
    if (!reviseState || reviseState.selected.length === 0) return;
    setReviseState({ ...reviseState, loading: true });
    try {
      if (reviseState.selected.length === 1) {
        const dt = reviseState.selected[0];
        const { data } = await api.post(`/projects/${project.id}/documents/${dt}/revise`, { instruction: reviseState.instruction });
        setReviseState({
          ...reviseState,
          stage: "review",
          docs: { [dt]: { original: data.original, revised: data.revised } },
          activeTab: dt,
          loading: false,
        });
      } else {
        const { data } = await api.post(`/projects/${project.id}/documents/revise/cascade`, {
          doc_types: reviseState.selected,
          instruction: reviseState.instruction,
        });
        setReviseState({
          ...reviseState,
          stage: "review",
          docs: data.docs,
          activeTab: reviseState.selected[0],
          loading: false,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setReviseState({ ...reviseState, loading: false });
    }
  };

  const patchRevisedContent = (dt, content) => {
    if (!reviseState?.docs) return;
    setReviseState({
      ...reviseState,
      docs: { ...reviseState.docs, [dt]: { ...reviseState.docs[dt], revised: content } },
    });
  };

  const acceptRevise = async () => {
    if (!reviseState?.docs) return;
    try {
      const types = Object.keys(reviseState.docs);
      if (types.length === 1) {
        const dt = types[0];
        await api.post(`/projects/${project.id}/documents/${dt}/revise/accept`, {
          revised_content: reviseState.docs[dt].revised,
          instruction: reviseState.instruction,
        });
      } else {
        const revisions = Object.fromEntries(types.map((dt) => [dt, reviseState.docs[dt].revised]));
        await api.post(`/projects/${project.id}/documents/revise/cascade/accept`, {
          revisions,
          instruction: reviseState.instruction,
        });
      }
      toast.success(`${types.length === 1 ? DOC_META[types[0]]?.label : `${types.length} documents`} updated · version snapshot saved`);
      setReviseState(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const lastContent = msgs.length ? msgs[msgs.length - 1].content : "";
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs.length, lastContent]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const q = input.trim();
    setInput("");
    setSending(true);
    // Optimistic user bubble + empty streaming AI bubble
    const startTs = new Date().toISOString();
    setMsgs((m) => [
      ...m,
      { role: "user", author: "You", content: q, ts: startTs },
      { role: "assistant", author: "AI Reviewer", content: "", ts: startTs, streaming: true },
    ]);
    try {
      const token = localStorage.getItem("propgen_token") || "";
      const res = await fetch(`${API}/projects/${project.id}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: q }),
      });
      if (!res.ok || !res.body) throw new Error(`Stream failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const raw of parts) {
          const line = raw.trim();
          if (!line.startsWith("data:")) continue;
          let evt;
          try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (evt.type === "delta") {
            setMsgs((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              if (last && last.streaming) {
                copy[copy.length - 1] = { ...last, content: last.content + evt.content };
              }
              return copy;
            });
          } else if (evt.type === "done") {
            setMsgs((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...evt.ai_message };
              return copy;
            });
          } else if (evt.type === "error") {
            toast.error(evt.message || "AI error");
          }
        }
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Chat failed");
      // Remove the streaming placeholder on error
      setMsgs((m) => (m.length && m[m.length - 1].streaming ? m.slice(0, -1) : m));
    }
    setSending(false);
  };

  const docContent = visibleDocs?.[docType]?.content || "";
  const docs = visibleDocs;

  return (
    <div className="h-[calc(100vh-165px)]" data-testid="review-step">
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel defaultSize={60} minSize={30}>
          <div className="h-full flex flex-col">
            <div className="px-6 py-3 border-b bg-card flex items-center gap-3">
              <div className="eyebrow">Reviewing</div>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="review-doc-select" className="h-8 w-56 border-2 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(docs).map((k) => <SelectItem key={k} value={k}>{DOC_META[k]?.label || k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className={`flex-1 min-h-0 overflow-auto ${docType === "internal" ? "pattern-internal" : ""}`}>
              <div className="mx-auto max-w-3xl bg-card border my-6 p-10">
                {docContent ? <Markdown text={docContent} /> : <div className="text-sm text-muted-foreground italic">Generate documents first.</div>}
              </div>
            </div>
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={40} minSize={25}>
          <div className="h-full flex flex-col bg-muted/30 border-l">
            <div className="px-4 py-3 border-b bg-card flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[var(--peak-blue)]" />
              <div className="font-display font-bold text-sm">AI Review Assistant</div>
              <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Internal only</span>
            </div>
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto p-4 space-y-3" data-testid="chat-messages">
              {msgs.length === 0 && (
                <div className="text-xs text-muted-foreground italic p-4 border border-dashed">
                  Ask about pricing tradeoffs, scope trims, unclear requirements, risk mitigation…
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} data-testid={`chat-msg-${i}`} className={`border p-3 text-sm ${m.role === "assistant" ? "bg-card border-neutral-300" : "bg-[var(--peak-blue)]/5 border-[var(--peak-blue)]/40"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                      {m.author}
                      {m.streaming && <span className="ml-2 text-[var(--peak-blue)] normal-case">· streaming…</span>}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground">{new Date(m.ts).toLocaleTimeString()}</div>
                  </div>
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.content}
                    {m.streaming && <span className="inline-block w-2 h-3.5 bg-[var(--peak-blue)] ml-0.5 align-middle animate-pulse" />}
                  </div>
                  {m.role === "assistant" && !m.streaming && m.content && (
                    <div className="mt-2 pt-2 border-t border-dashed border-neutral-300 flex items-center gap-2">
                      <button
                        onClick={() => openRevise(m.content)}
                        data-testid={`apply-to-doc-btn-${i}`}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[var(--peak-blue)] text-[var(--peak-blue)] hover:bg-[var(--peak-blue)] hover:text-white transition-colors duration-150"
                      >
                        <Zap className="w-3 h-3" /> Apply to {DOC_META[docType]?.label || docType}
                      </button>
                      <span className="text-[10px] text-muted-foreground font-mono">generates revision + diff</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t p-3 bg-card">
              <div className="flex gap-2">
                <Textarea
                  rows={2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                  placeholder="Ask the reviewer… (Cmd+Enter to send)"
                  data-testid="chat-input"
                  className="border-2 text-sm font-mono resize-none"
                />
                <Button onClick={send} disabled={sending || !input.trim()} data-testid="chat-send-btn" className="h-auto bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide px-4">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <Dialog open={!!reviseState} onOpenChange={(v) => { if (!v) setReviseState(null); }}>
        <DialogContent className="max-w-5xl w-[92vw]" data-testid="revise-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
              <Zap className="w-5 h-5 text-[var(--peak-blue)]" />
              {reviseState?.stage === "review"
                ? `Review revision${(reviseState.docs && Object.keys(reviseState.docs).length > 1) ? "s" : ""}`
                : "Apply revision"}
            </DialogTitle>
          </DialogHeader>
          {reviseState && (
            <div className="space-y-4">
              <div className="border-l-2 border-[var(--peak-blue)] bg-[var(--peak-blue)]/5 px-3 py-2 text-xs" data-testid="revise-instruction">
                <div className="eyebrow mb-1">Applying instruction</div>
                <div className="line-clamp-3 whitespace-pre-wrap">{reviseState.instruction}</div>
              </div>

              {reviseState.stage === "select" && (
                <div className="space-y-3">
                  <div className="eyebrow">Cascade to which documents?</div>
                  <div className="grid grid-cols-2 gap-2">
                    {reviseState.availableDocs.map((dt) => {
                      const checked = reviseState.selected.includes(dt);
                      return (
                        <button
                          key={dt}
                          type="button"
                          onClick={() => toggleReviseDoc(dt)}
                          data-testid={`revise-doc-checkbox-${dt}`}
                          className={`flex items-center justify-between border-2 px-3 py-2.5 text-sm transition-colors duration-150 ${checked ? "border-[var(--peak-blue)] bg-[var(--peak-blue)]/10" : "border-neutral-300 hover:border-black"}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-4 h-4 border-2 flex items-center justify-center ${checked ? "border-[var(--peak-blue)] bg-[var(--peak-blue)]" : "border-neutral-400"}`}>
                              {checked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="font-medium truncate">{DOC_META[dt]?.label || dt}</span>
                          </div>
                          {dt === "internal" && (
                            <span className="text-[10px] font-mono uppercase tracking-wider text-red-700 border border-red-500 px-1.5 py-0.5">Internal</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {reviseState.selected.length > 1
                      ? `Cascade mode · GPT-5.2 will revise ${reviseState.selected.length} docs in parallel and snapshot once atomically.`
                      : "Single doc mode · standard revise + snapshot."}
                  </div>
                </div>
              )}

              {reviseState.stage === "review" && reviseState.loading && (
                <div className="py-16 text-center">
                  <div className="inline-flex items-center gap-2 text-sm text-muted-foreground font-mono uppercase tracking-wider">
                    <div className="w-2 h-2 bg-[var(--peak-blue)] animate-pulse" />
                    GPT-5.2 rewriting {reviseState.selected.length} document{reviseState.selected.length === 1 ? "" : "s"}…
                  </div>
                </div>
              )}

              {reviseState.stage === "review" && !reviseState.loading && reviseState.docs && (
                <>
                  {Object.keys(reviseState.docs).length > 1 && (
                    <div className="flex flex-wrap gap-0 border-b -mb-px" data-testid="revise-doc-tabs">
                      {Object.keys(reviseState.docs).map((dt) => (
                        <button
                          key={dt}
                          onClick={() => setReviseState({ ...reviseState, activeTab: dt })}
                          data-testid={`revise-tab-${dt}`}
                          className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 transition-colors duration-150 ${
                            reviseState.activeTab === dt
                              ? "border-[var(--peak-blue)] text-foreground"
                              : "border-transparent text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {dt === "internal" && <AlertTriangle className="w-3 h-3 mr-1.5 inline-block text-red-600" />}
                          {DOC_META[dt]?.label || dt}
                        </button>
                      ))}
                    </div>
                  )}
                  {reviseState.activeTab && (
                    <DiffView
                      original={reviseState.docs[reviseState.activeTab].original}
                      revised={reviseState.docs[reviseState.activeTab].revised}
                    />
                  )}
                </>
              )}

              {reviseState.stage === "review" && !reviseState.loading && (
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-t pt-3">
                  {Object.keys(reviseState.docs || {}).length > 1
                    ? "Accepting will snapshot once and apply all changes atomically."
                    : "Accepting will snapshot once and apply this change."}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <button
              onClick={() => setReviseState(null)}
              data-testid="revise-discard-btn"
              className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors"
            >
              {reviseState?.stage === "review" ? "Discard" : "Cancel"}
            </button>
            {reviseState?.stage === "review" && (
              <button
                onClick={() => setReviseState({ ...reviseState, stage: "select", docs: null, activeTab: null })}
                data-testid="revise-back-btn"
                className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors"
              >
                ← Back
              </button>
            )}
            {reviseState?.stage === "select" ? (
              <Button
                onClick={runRevise}
                disabled={!reviseState || reviseState.loading || reviseState.selected.length === 0}
                data-testid="revise-generate-btn"
                className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {reviseState?.loading
                  ? "…"
                  : `Generate ${reviseState?.selected?.length || 0} Revision${(reviseState?.selected?.length || 0) === 1 ? "" : "s"}`}
              </Button>
            ) : (
              <Button
                onClick={acceptRevise}
                disabled={!reviseState || reviseState.loading || !reviseState.docs}
                data-testid="revise-accept-btn"
                className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold uppercase text-xs tracking-wide"
              >
                <Check className="w-4 h-4 mr-2" /> Accept &amp; Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
   Step 6 · Versions
   ============================================================ */
function VersionsStep({ project, refresh, canSeeInternal = true }) {
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [compare, setCompare] = useState(null); // { version, docType, loading, snapshotContent, currentContent }
  const visibleDocs = filterDocsForUser(project.documents, canSeeInternal);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/versions`);
      setVersions(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [project.id]);

  const snapshot = async () => {
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/versions`);
      toast.success("Version saved");
      await load();
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const openCompare = async (version) => {
    // Prefer the doc that was changed in this snapshot; else use first available doc
    const availableDocs = Object.keys(visibleDocs);
    const changedDoc = version.doc_type_changed && availableDocs.includes(version.doc_type_changed) ? version.doc_type_changed : null;
    const targetDoc = changedDoc || availableDocs[0] || "proposal";
    setCompare({ version, docType: targetDoc, loading: true, snapshotContent: "", currentContent: "" });
    try {
      const { data } = await api.get(`/projects/${project.id}/versions/${version.id}`);
      const snap = data.documents?.[targetDoc]?.content || "";
      const curr = project.documents?.[targetDoc]?.content || "";
      setCompare({ version, docType: targetDoc, loading: false, snapshotContent: snap, currentContent: curr });
    } catch (e) {
      toast.error(formatApiError(e));
      setCompare(null);
    }
  };

  const switchCompareDoc = async (docType) => {
    if (!compare) return;
    setCompare({ ...compare, docType, loading: true });
    try {
      const { data } = await api.get(`/projects/${project.id}/versions/${compare.version.id}`);
      const snap = data.documents?.[docType]?.content || "";
      const curr = project.documents?.[docType]?.content || "";
      setCompare({ version: compare.version, docType, loading: false, snapshotContent: snap, currentContent: curr });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8" data-testid="versions-step">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Step 6</div>
          <h2 className="font-display text-2xl font-black tracking-tight mt-1">Version history</h2>
          <p className="text-sm text-muted-foreground mt-1">Snapshot the current state after team review.</p>
        </div>
        <Button onClick={snapshot} disabled={saving} data-testid="save-version-btn" className="h-10 bg-[var(--peak-blue)] hover:bg-[#0029C7] text-white font-display font-bold text-xs uppercase tracking-wide">
          <History className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Version"}
        </Button>
      </div>

      <div className="mt-6" data-testid="timeline-wrap">
        <Timeline
          versions={versions}
          currentTs={project.updated_at}
          onSelect={openCompare}
          selectedId={compare?.version?.id}
        />
      </div>

      <div className="border bg-card mt-6" data-testid="versions-list">
        <div className="grid grid-cols-[80px_1fr_240px_140px_120px_100px] items-center px-4 py-2 border-b bg-muted/50 text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
          <div>Kind</div><div>Version / Reason</div><div>By</div><div className="text-right">Ext. Price</div><div className="text-right">Hours</div><div className="text-right">Status</div>
        </div>
        {versions.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">No versions saved yet. They auto-snapshot before every AI revision, or you can save one manually.</div>
        )}
        {versions.map((v, i) => {
          const isAuto = v.kind === "auto_pre_revision";
          return (
            <button key={v.id} onClick={() => openCompare(v)} className={`w-full grid grid-cols-[80px_1fr_240px_140px_120px_100px] items-start px-4 py-3 border-b last:border-b-0 text-sm text-left transition-colors hover:bg-muted/50 ${isAuto ? "bg-[var(--peak-blue)]/5" : ""}`} data-testid={`version-row-${i}`}>
              <div>
                <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border ${isAuto ? "border-[var(--peak-blue)] text-[var(--peak-blue)] bg-white" : "border-neutral-400 text-neutral-700"}`}>
                  {isAuto ? "AI" : "Manual"}
                </span>
              </div>
              <div className="min-w-0">
                <div className="font-mono text-xs">v{versions.length - i} · {new Date(v.created_at).toLocaleString()}</div>
                {v.reason && (
                  <div className="text-xs mt-1 text-foreground leading-relaxed">
                    {isAuto && <Zap className="w-3 h-3 inline-block mr-1 text-[var(--peak-blue)] align-[-2px]" />}
                    {v.reason}
                  </div>
                )}
                {v.doc_type_changed && (
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-1">
                    changed → {DOC_META[v.doc_type_changed]?.label || v.doc_type_changed}
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate pr-2">{v.created_by}</div>
              <div className="font-mono text-xs text-right">{money(v.estimate?.external_price)}</div>
              <div className="font-mono text-xs text-right">{hours(v.estimate?.total_hours)}</div>
              <div className="text-right"><span className="text-[10px] font-mono uppercase tracking-wider border px-2 py-0.5">{v.status || "—"}</span></div>
            </button>
          );
        })}
      </div>

      <Dialog open={!!compare} onOpenChange={(v) => { if (!v) setCompare(null); }}>
        <DialogContent className="max-w-6xl w-[94vw]" data-testid="version-compare-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-3">
              <History className="w-5 h-5 text-[var(--peak-blue)]" />
              Compare snapshot vs current
              {compare?.version && (
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground border px-2 py-0.5">
                  v{compare && (versions.length - versions.findIndex((x) => x.id === compare.version.id))}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {compare?.version && (
            <div className="space-y-3">
              <div className="border-l-2 border-[var(--peak-blue)] bg-[var(--peak-blue)]/5 px-3 py-2 text-xs" data-testid="compare-meta">
                <div className="eyebrow mb-1">{compare.version.kind === "auto_pre_revision" ? "AI-triggered snapshot" : "Manual snapshot"}</div>
                <div className="whitespace-pre-wrap">{compare.version.reason || "—"}</div>
                {compare.version.instruction && compare.version.instruction !== compare.version.reason && (
                  <div className="mt-2 pt-2 border-t border-[var(--peak-blue)]/30">
                    <div className="eyebrow mb-1">Instruction</div>
                    <div className="whitespace-pre-wrap text-[11px]">{compare.version.instruction}</div>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-[var(--peak-blue)]/30 flex items-center gap-2">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Viewing</span>
                  <Select value={compare.docType} onValueChange={switchCompareDoc}>
                    <SelectTrigger data-testid="compare-doc-select" className="h-7 w-56 border-2 text-xs font-mono uppercase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(visibleDocs).map((k) => <SelectItem key={k} value={k}>{DOC_META[k]?.label || k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground ml-auto">
                    Snapshot → Current
                  </span>
                </div>
              </div>
              {compare.loading ? (
                <div className="py-16 text-center text-sm font-mono uppercase tracking-wider text-muted-foreground">Loading snapshot…</div>
              ) : compare.snapshotContent || compare.currentContent ? (
                <DiffView original={compare.snapshotContent} revised={compare.currentContent} />
              ) : (
                <div className="border-2 border-dashed p-10 text-center text-sm text-muted-foreground">
                  This snapshot has no content for the selected document.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setCompare(null)} data-testid="compare-close-btn" className="h-10 px-4 text-xs uppercase tracking-wider font-mono border-2 hover:bg-muted transition-colors">
              Close
            </button>
            <Button
              onClick={async () => {
                if (!compare?.version) return;
                if (!window.confirm(`Restore this snapshot? Current state will be auto-snapshotted first, then all documents & estimates will be overwritten with v${versions.length - versions.findIndex((x) => x.id === compare.version.id)}.`)) return;
                try {
                  await api.post(`/projects/${project.id}/versions/${compare.version.id}/restore`);
                  toast.success("Version restored · pre-restore snapshot saved");
                  setCompare(null);
                  await load();
                  await refresh();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              disabled={!compare?.version || compare?.loading}
              data-testid="compare-restore-btn"
              className="h-10 bg-black hover:bg-[var(--peak-blue)] text-white font-display font-bold uppercase text-xs tracking-wide transition-colors duration-200"
            >
              <History className="w-4 h-4 mr-2" /> Restore This Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
